// ArticleComments — 文章评论区(pen WsKjU + Z74WW;2026-06-12 spec §5.2)。
// header(评论+总数)/ 平铺列表(头像+昵称+内容,新→旧)/ 输入行(field + 发布)。
// gate:未登录 → onRequireLogin;无 can_comment → onRequireVip。

import { forwardRef, useState } from 'react'
import { useArticleComments, usePostComment, type CommentRow } from '../_lib/comment-api'
import { resourceUrl } from '../_lib/resource-node-pref'

type Props = {
  articleId: string
  canComment: boolean
  isLoggedIn: boolean
  onRequireLogin: () => void
  onRequireVip: () => void
}

const PAGE = 20

export const ArticleComments = forwardRef<HTMLDivElement, Props>(function ArticleComments(
  { articleId, canComment, isLoggedIn, onRequireLogin, onRequireVip },
  ref,
) {
  const [pages, setPages] = useState(1)
  const [draft, setDraft] = useState('')
  // offset 分页:一次取 pages*PAGE(invalidate 后整段刷新,简单可靠)
  // 匿名不发请求(GET comments 在 guarded 段,401 会触发全局"登录已失效"toast)
  const listQ = useArticleComments(articleId, 0, pages * PAGE, isLoggedIn)
  const postMut = usePostComment(articleId)

  const items: CommentRow[] = listQ.data?.items ?? []
  const total = listQ.data?.total ?? 0

  const gate = (): boolean => {
    if (!isLoggedIn) {
      onRequireLogin()
      return true
    }
    if (!canComment) {
      onRequireVip()
      return true
    }
    return false
  }

  const submit = async () => {
    const content = draft.trim()
    if (!content || postMut.isPending) return
    if (gate()) return
    await postMut.mutateAsync(content)
    setDraft('')
  }

  return (
    <div ref={ref} data-testid="article-comments" className="flex flex-col gap-5 px-5 pb-8 pt-2">
      <div className="h-px w-full bg-[#F3F4F6]" />
      <div className="flex items-center gap-2">
        <h3 className="text-[17px] font-bold text-[#1A1A1A]">评论</h3>
        <span data-testid="comment-total" className="text-[15px] font-semibold text-[#9CA3AF]">{total}</span>
      </div>

      <div className="flex flex-col gap-[18px]">
        {items.map((c) => (
          <div key={c.id} className="flex gap-3" data-testid="comment-item">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-full bg-[#C9E4DE] text-[12px] text-[#1A1A1A]">
              {c.avatar ? <img src={resourceUrl(c.avatar)} alt="" className="h-full w-full object-cover" /> : c.nickname.slice(0, 1)}
            </span>
            <div className="flex min-w-0 flex-1 flex-col gap-1.5">
              <span className="text-[13px] font-semibold text-[#1A1A1A]">{c.nickname}</span>
              <p className="break-words text-[14px] leading-[1.6] text-[#374151]">{c.content}</p>
            </div>
          </div>
        ))}
        {!listQ.isLoading && items.length === 0 && (
          <p className="py-2 text-center text-[13px] text-[#9CA3AF]">还没有评论,来抢沙发</p>
        )}
        {items.length < total && (
          <button
            type="button"
            data-testid="comment-more"
            onClick={() => setPages((p) => p + 1)}
            className="py-1 text-center text-[13px] text-[#19caad]"
          >
            查看更多
          </button>
        )}
      </div>

      {/* 输入行(Z74WW) */}
      <div className="flex items-center gap-2.5">
        <input
          data-testid="comment-input"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onFocus={(e) => {
            if (gate()) e.currentTarget.blur()
          }}
          maxLength={500}
          placeholder="写下你的评论…"
          className="min-w-0 flex-1 rounded-[22px] border border-[#EEEFF1] bg-[#F6F7F8] px-4 py-[11px] text-[14px] text-[#1A1A1A] placeholder-[#9CA3AF] outline-none"
        />
        <button
          type="button"
          data-testid="comment-submit"
          onClick={() => void submit()}
          disabled={postMut.isPending || !draft.trim()}
          className="shrink-0 rounded-[22px] bg-[#19caad] px-5 py-[11px] text-[14px] font-semibold text-white disabled:opacity-50"
        >
          发布
        </button>
      </div>
    </div>
  )
})
